import { useState, useRef, useEffect, useCallback } from 'react'
import { Icon } from '../Icon'
import styles from './TimezoneSelect.module.css'

interface TzOption { value: string; label: string }

interface Props {
  value: string
  options: TzOption[]
  onChange: (value: string) => void
}

export function TimezoneSelect({ value, options, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const selected = options.find(o => o.value === value)
  const filtered = query
    ? options.filter(o =>
        o.label.toLowerCase().includes(query.toLowerCase()) ||
        o.value.toLowerCase().includes(query.toLowerCase())
      )
    : options

  function openDropdown() {
    setOpen(true)
    setQuery('')
    setHighlighted(Math.max(0, options.findIndex(o => o.value === value)))
    setTimeout(() => searchRef.current?.focus(), 10)
  }

  function select(val: string) {
    onChange(val)
    setOpen(false)
    setQuery('')
  }

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!open) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlighted]) select(filtered[highlighted].value) }
    else if (e.key === 'Escape') setOpen(false)
  }, [open, filtered, highlighted])

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${highlighted}"]`) as HTMLElement
    el?.scrollIntoView({ block: 'nearest' })
  }, [highlighted])

  useEffect(() => { setHighlighted(0) }, [query])

  return (
    <div className={styles.wrap} ref={wrapRef} onKeyDown={onKeyDown}>
      <button type="button" className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        onClick={openDropdown} aria-haspopup="listbox" aria-expanded={open}>
        <Icon name="schedule" size={18} className={styles.triggerIcon} />
        <span className={styles.triggerLabel}>{selected?.label ?? value}</span>
        <Icon name="expand_more" size={18} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} />
      </button>
      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrap}>
            <Icon name="search" size={16} className={styles.searchIcon} />
            <input ref={searchRef} type="text" className={styles.search}
              placeholder="Search timezones…" value={query}
              onChange={e => setQuery(e.target.value)} aria-label="Search timezones" />
          </div>
          <ul className={styles.list} ref={listRef} role="listbox">
            {filtered.length === 0 && <li className={styles.empty}>No timezones match</li>}
            {filtered.map((opt, idx) => {
              const isSel = opt.value === value
              const isHi = idx === highlighted
              return (
                <li key={opt.value} data-idx={idx} role="option" aria-selected={isSel}
                  className={`${styles.option} ${isSel ? styles.optionSelected : ''} ${isHi ? styles.optionHighlighted : ''}`}
                  onMouseEnter={() => setHighlighted(idx)}
                  onMouseDown={e => { e.preventDefault(); select(opt.value) }}>
                  <span className={styles.optionLabel}>{opt.label}</span>
                  {isSel && <Icon name="check" size={16} className={styles.optionCheck} />}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
